/**
 * Self-hosted implementation of RealtimeAdapter.
 * Opens a WebSocket to a custom server that streams
 * PostgreSQL LISTEN/NOTIFY events.
 *
 * WebSocket URL is read from NEXT_PUBLIC_SELF_HOSTED_WS_URL.
 */

import type { RealtimeAdapter, RealtimeEvent, RealtimePayload, RealtimeSubscription } from '../interfaces';

const WS_URL = () =>
  process.env.NEXT_PUBLIC_SELF_HOSTED_WS_URL ?? 'ws://localhost:4001';

function getToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('nexus_access_token');
}

export class SelfHostedRealtimeAdapter implements RealtimeAdapter {
  subscribe<T = Record<string, unknown>>(
    channel: string,
    table: string,
    event: RealtimeEvent,
    callback: (payload: RealtimePayload<T>) => void
  ): RealtimeSubscription {
    const token = getToken();
    const url = `${WS_URL()}?channel=${encodeURIComponent(channel)}&table=${encodeURIComponent(table)}&event=${encodeURIComponent(event)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;

    let ws: WebSocket | null = null;
    let closed = false;

    const connect = () => {
      if (closed) return;
      ws = new WebSocket(url);

      ws.onmessage = (msg) => {
        try {
          const raw = JSON.parse(msg.data);
          callback({
            eventType: raw.event_type ?? event,
            table: raw.table ?? table,
            schema: raw.schema ?? 'public',
            new: raw.new ?? null,
            old: raw.old ?? null,
          });
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (!closed) {
          // Reconnect after 3 s
          setTimeout(connect, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[SelfHostedRealtime] WebSocket error:', err);
      };
    };

    connect();

    return {
      unsubscribe: () => {
        closed = true;
        ws?.close();
      },
    };
  }

  async broadcast(channel: string, event: string, payload: Record<string, unknown>): Promise<void> {
    const token = getToken();
    const url = `${WS_URL()}?channel=${encodeURIComponent(channel)}`;
    let ws = new WebSocket(url);
    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'broadcast', event, payload, token }));
        ws.close();
        resolve();
      };
      ws.onerror = reject;
    });
  }
}
